/**
 * Referral program constants + pure helpers, shared across the API (signup, cron,
 * /v1/referrals, admin) and the web/admin UIs. A qualifying referral — one whose
 * referred account signed up, uploaded ≥2 statements, AND added a payment method —
 * earns the REFERRER a flat $25 credit, capped at 10 qualifying referrals per
 * calendar year ($250). Referrer-only.
 */

/** Flat credit granted to the referrer per qualifying referral (cents). */
export const REFERRAL_CREDIT_CENTS = 2500;
/** Max credited referrals per referrer per calendar year (10 × $25 = $250). */
export const REFERRAL_CAP_PER_YEAR = 10;

/** Linear display stage derived from the milestone timestamps. */
export type ReferralStage = 'signed_up' | 'statements' | 'payment' | 'qualified' | 'credited';

export interface ReferralMilestones {
  statementsAt: number | Date | null;
  paymentAt: number | Date | null;
  qualifiedAt: number | Date | null;
  creditedAt: number | Date | null;
}

const STAGE_LABELS: Record<ReferralStage, string> = {
  signed_up: 'Signed up',
  statements: '2+ statements',
  payment: 'Payment added',
  qualified: 'Qualified',
  credited: 'Credited',
};

/** The furthest stage a referral has reached (the row existing = "signed up"). */
export function referralStage(m: ReferralMilestones): ReferralStage {
  if (m.creditedAt) return 'credited';
  if (m.qualifiedAt) return 'qualified';
  if (m.paymentAt) return 'payment';
  if (m.statementsAt) return 'statements';
  return 'signed_up';
}

export function referralStageLabel(stage: ReferralStage): string {
  return STAGE_LABELS[stage];
}

/** Whether a now-qualified referral may be credited, given how many the referrer
 *  has already been credited this calendar year. Enforces the annual cap. */
export function canCreditReferral(creditedThisYear: number): boolean {
  return creditedThisYear < REFERRAL_CAP_PER_YEAR;
}
